import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { autoUpdateNotesAsync, retryNotesAsync, updateLocalNoteValue } from '../../../../redux/flows/portal/notes'
import { useAppDispatch, type RootState } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'

/**
 * NotesView Hook - All state in Redux
 *
 * Flow:
 * 1. User types → updateLocalNoteValue (Redux) → debounce → autoUpdateNotesAsync → API → Redux updates
 * 2. User clicks retry → retryNotesAsync → API → Redux updates → localNoteValues syncs
 * 3. User selects note → setCurrentNote → localNoteValues initialized from Redux
 */

export const useNotesView = () => {
  const dispatch = useAppDispatch()

  // ===== REDUX STATE (Single Source of Truth) =====
  const currentNote = useSelector((state: RootState) => state.notesPortal.currentNote)
  const pendingNotes = useSelector((state: RootState) => state.notesPortal.notes)
  const patientNotes = useSelector((state: RootState) => state.notesPortal.patientNotes)
  const localNoteValues = useSelector((state: RootState) => state.notesPortal.localNoteValues)
  const retryNotesStatus = useSelector((state: RootState) => state.notesPortal.retryNotesStatus)
  const autoUpdateNoteStatus = useSelector((state: RootState) => state.notesPortal.autoUpdateNoteStatus)
  const historySelectedNoteId = useSelector((state: RootState) => state.notesPortal.historySelectedNoteId)

  // Debounce timer for auto-save
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // Track which field is being retried (for loading state)
  const retryingFieldRef = useRef<string | null>(null)

  // ===== COMPUTED VALUES =====

  /**
   * Get current note's local typing values from Redux
   * These are the values user is typing (optimistic UI)
   */
  const currentLocalValues = useMemo(() => {
    if (!currentNote?.id) {
      return { subjective: '', objective: '', assessment: '', plan: '' }
    }
    return (
      localNoteValues[currentNote.id] || {
        subjective: currentNote.subjective || '',
        objective: currentNote.objective || '',
        assessment: currentNote.assessment || '',
        plan: currentNote.plan || '',
      }
    )
  }, [currentNote, localNoteValues])

  /**
   * Get patient notes history for the current note's patient
   */
  const patientHistoryNotes = useMemo(() => {
    if (!currentNote?.patient?.id) return []
    const patientNoteData = patientNotes[currentNote.patient.id]
    return patientNoteData?.notes || []
  }, [currentNote?.patient?.id, patientNotes])

  /**
   * Determine if the current view is read-only
   * Read-only when:
   * - No current note
   * - Note status is 'accepted' (history notes that are accepted are read-only)
   *
   * Note: Pending notes are always editable, regardless of where they come from
   */
  const isReadOnly = useMemo(() => {
    if (!currentNote) return true
    return currentNote.status === 'accepted'
  }, [currentNote])

  /**
   * Check if we have a valid note to display
   */
  const hasSelectedNote = Boolean(currentNote)

  // ===== AUTO-SAVE WITH DEBOUNCE =====

  /**
   * Auto-save function with debounce
   * Saves changes to Redux after 1 second of no typing
   */
  const autoSave = useCallback(
    (field: 'subjective' | 'objective' | 'assessment' | 'plan', value: string) => {
      if (!currentNote || isReadOnly) return

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }

      // Set new timer
      debounceTimer.current = setTimeout(() => {
        // Get the latest local values from Redux and override the field that just changed
        const baseValues = localNoteValues[currentNote.id] || {
          subjective: currentNote.subjective || '',
          objective: currentNote.objective || '',
          assessment: currentNote.assessment || '',
          plan: currentNote.plan || '',
        }

        // Ensure the most recent typed value is included (avoids dropping last character)
        const latestValues = {
          ...baseValues,
          [field]: value,
        }

        // Dispatch auto-update with all current values
        dispatch(
          autoUpdateNotesAsync({
            updatedNotes: {
              ...currentNote,
              subjective: latestValues.subjective,
              objective: latestValues.objective,
              assessment: latestValues.assessment,
              plan: latestValues.plan,
              status: 'pending',
            },
          }),
        )
      }, 500)
    },
    [currentNote, isReadOnly, localNoteValues, dispatch],
  )

  // ===== HANDLERS =====

  /**
   * Handle SOAP field changes
   * Updates Redux localNoteValues immediately (optimistic UI) and triggers debounced auto-save
   */
  const handleSubjectiveChange = useCallback(
    (value: string) => {
      if (!currentNote || isReadOnly) return
      dispatch(updateLocalNoteValue({ noteId: currentNote.id, field: 'subjective', value }))
      autoSave('subjective', value)
    },
    [currentNote, isReadOnly, dispatch, autoSave],
  )

  const handleObjectiveChange = useCallback(
    (value: string) => {
      if (!currentNote || isReadOnly) return
      dispatch(updateLocalNoteValue({ noteId: currentNote.id, field: 'objective', value }))
      autoSave('objective', value)
    },
    [currentNote, isReadOnly, dispatch, autoSave],
  )

  const handleAssessmentChange = useCallback(
    (value: string) => {
      if (!currentNote || isReadOnly) return
      dispatch(updateLocalNoteValue({ noteId: currentNote.id, field: 'assessment', value }))
      autoSave('assessment', value)
    },
    [currentNote, isReadOnly, dispatch, autoSave],
  )

  const handlePlanChange = useCallback(
    (value: string) => {
      if (!currentNote || isReadOnly) return
      dispatch(updateLocalNoteValue({ noteId: currentNote.id, field: 'plan', value }))
      autoSave('plan', value)
    },
    [currentNote, isReadOnly, dispatch, autoSave],
  )

  /**
   * Handle retry for a specific SOAP field
   * Calls API to regenerate the field value
   */
  const handleRetry = useCallback(
    (field: 'subjective' | 'objective' | 'assessment' | 'plan') => {
      if (!currentNote?.id || isReadOnly) return

      retryingFieldRef.current = field
      dispatch(retryNotesAsync({ notesId: currentNote.id, noteToRetry: field }))
    },
    [currentNote, isReadOnly, dispatch],
  )

  // ===== RETRY COMPLETION HANDLER =====

  /**
   * When retry completes, reset retrying state
   * Redux will automatically sync localNoteValues with the updated note
   */
  useEffect(() => {
    if (retryNotesStatus === APIStatus.fulfilled || retryNotesStatus === APIStatus.rejected) {
      retryingFieldRef.current = null
    }
  }, [retryNotesStatus])

  // ===== CLEANUP =====

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  // ===== RETURN VALUES =====

  return {
    // Current values (from Redux localNoteValues for optimistic UI)
    subjective: currentLocalValues.subjective,
    objective: currentLocalValues.objective,
    assessment: currentLocalValues.assessment,
    plan: currentLocalValues.plan,

    // Handlers
    handleSubjectiveChange,
    handleObjectiveChange,
    handleAssessmentChange,
    handlePlanChange,

    // Retry handlers
    handleRetrySubjective: () => handleRetry('subjective'),
    handleRetryObjective: () => handleRetry('objective'),
    handleRetryAssessment: () => handleRetry('assessment'),
    handleRetryPlan: () => handleRetry('plan'),

    // Retry loading states
    isRetryingSubjective: retryNotesStatus === APIStatus.pending && retryingFieldRef.current === 'subjective',
    isRetryingObjective: retryNotesStatus === APIStatus.pending && retryingFieldRef.current === 'objective',
    isRetryingAssessment: retryNotesStatus === APIStatus.pending && retryingFieldRef.current === 'assessment',
    isRetryingPlan: retryNotesStatus === APIStatus.pending && retryingFieldRef.current === 'plan',

    // Patient history notes (for history panel)
    currentPatientNote: patientHistoryNotes,

    // Status
    autoUpdateNoteStatus,
    retryNotesStatus,

    // UI state
    hasSelectedNote,
    isReadOnly,
  }
}
